/* eslint-disable max-lines */
import { useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Stack } from "@altimateai/lego";
import { cn } from "@/lib/utils";
import { getAllQueryGroups, getQueryFilters } from "@/helpers/apis";
import { useAppState } from "@/modules/app/useAppContext";
import { formatNumber } from "@/uiCore";
import { getInstanceName } from "@/helpers/utils";
import DataTable from "@/components/tables/DataTable";
import { useTablePagination } from "@/components/tables/hooks/useTablePagination";
import { Paginate } from "@/components/paginate";
import {
  FilterType,
  TableHeaderType,
  type APIFilterParams,
  createAPIDropdownFilter,
} from "@/components/tables/types";
import { FilterApplicableFor } from "@/components/tags/types";
import { ColorInsightTags } from "@/components/tags";
import { GET_ALL_QUERY_GROUPS } from "../constants";
import { FilterEntityType, QueryFilters, QueryGroup, QueryTags } from "../types";
import QueryTextWithHover from "../QueryText";
import InsightLabel from "../InsightLabel";
import GroupsDownload from "./GroupsDownload";
import GroupsHeader from "./GroupsHeader";
import { useGroupsFilters } from "./useGroupsFilters";

interface GroupsListWithFilterProps {
  getRowUrl: (group: QueryGroup) => string;
  getRowState?: (group: QueryGroup) => Record<string, unknown>;
  onGroupFound?: (group: QueryGroup) => void;
  queryHash?: string;
}

const GroupsListWithFilter = ({
  getRowUrl,
  getRowState,
  onGroupFound,
  queryHash = "",
}: GroupsListWithFilterProps) => {
  const { currency, datastores = [] } = useAppState();

  // Pagination from context
  const {
    page,
    pageSize,
    setPage,
    setPageSize: updatePageSize,
    resetPage,
    setTotalPages,
    setTotalItems,
    totalItems,
    totalPages,
  } = useTablePagination();

  // Use filters hook
  const { filterParams, isInitialized } = useGroupsFilters({
    queryHash,
  });

  // Fetch query groups
  const {
    data: groups = {
      total: 0,
      items: [],
      pages: 1,
    },
    isFetching: isLoading,
  } = useQuery({
    queryKey: [GET_ALL_QUERY_GROUPS, filterParams],
    queryFn: () => getAllQueryGroups(filterParams),
    enabled: isInitialized,
  });

  // Update pagination totals when query data changes
  useEffect(() => {
    if (groups) {
      setTotalItems(groups.total);
      setTotalPages(groups.pages);
    }
  }, [groups, setTotalItems, setTotalPages]);

  // Handle navigation when queryHash matches
  useEffect(() => {
    if (queryHash && groups && onGroupFound) {
      const group = groups.items.find(item => item.query_hash === queryHash);
      if (group) {
        onGroupFound(group);
      }
    }
  }, [groups, queryHash, onGroupFound]);

  // Factory function for API filter dropdowns
  const createFilterApiFunction =
    (entityType: FilterEntityType) =>
    async ({ prefix, page, size }: APIFilterParams): Promise<QueryFilters | undefined> =>
      getQueryFilters({
        entity_type: entityType,
        prefix,
        page,
        size,
        filter_type: "query_group",
      });

  const getWarehousesFilter = createFilterApiFunction(FilterEntityType.WAREHOUSES);
  const getUsersFilter = createFilterApiFunction(FilterEntityType.USERS);
  const getTagsFilter = createFilterApiFunction(FilterEntityType.TAGS);

  // Table headers with filters
  const headers: TableHeaderType<QueryGroup>[] = useMemo(
    () => [
      {
        id: 1,
        label: "Group",
        width: "250px",
        uid: "query_group",
        render: item => (
          <div className="al-break-all">
            <QueryTextWithHover queryHash={item.query_hash} queryText={item.query_hash} />
          </div>
        ),
      },
      {
        id: 2,
        label: "Total run",
        width: "100px",
        uid: "total_run",
        sort: {
          sortSearchKey: "sortKey",

          sortParamValue: "count",
        },
        render: item => <div className="al-break-words">{item.total_run}</div>,
      },
      {
        id: 3,
        label: "Avg. execution time",
        uid: "avg_exec_time",
        width: "140px",
        sort: {
          sortSearchKey: "sortKey",

          sortParamValue: "avg_execution_time",
        },
        render: item => <div className="al-break-words">{item.avg_exec_time}</div>,
      },
      {
        id: 4,
        label: "Avg. cost",
        width: "100px",
        uid: "avg_cost",
        render: item => (
          <div className="al-break-words">{formatNumber(item.avg_cost, { currency })}</div>
        ),
      },
      {
        id: 5,
        label: "Total cost",
        width: "100px",
        uid: "total_cost",
        sort: {
          sortSearchKey: "sortKey",
        },
        render: item => (
          <div className="al-break-words">{formatNumber(item.total_cost, { currency })}</div>
        ),
      },
      {
        id: 6,
        label: "Annualized cost",
        uid: "annualized_cost",
        width: "130px",
        sort: {
          sortSearchKey: "sortKey",
        },
        render: item => (
          <div className="al-break-words">{formatNumber(item.annualized_cost, { currency })}</div>
        ),
      },
      {
        id: 7,
        label: "Warehouses",
        uid: "warehouses",
        width: "150px",
        filter: createAPIDropdownFilter({
          filterType: FilterType.API_DROPDOWN,
          searchKey: "warehouses",
          apiFunction: getWarehousesFilter,
          pageSize: 50,
          seeMore: true,
          extractorFunction: data => {
            return (
              data?.warehouses?.map((warehouse: string) => ({
                label: warehouse,
                value: warehouse,
              })) || []
            );
          },
        }),
        render: item => (
          <div className="al-break-words lines-2">{item.warehouses?.join(", ") ?? ""}</div>
        ),
      },
      {
        id: 8,
        label: "Users",
        uid: "users",
        width: "120px",
        filter: createAPIDropdownFilter({
          filterType: FilterType.API_DROPDOWN,
          searchKey: "users",
          apiFunction: getUsersFilter,
          pageSize: 50,
          seeMore: true,
          extractorFunction: data => {
            return (
              data?.users?.map((user: string) => ({
                label: user,
                value: user,
              })) || []
            );
          },
        }),
        render: item => (
          <div className="al-break-words lines-2">{item.users?.join(", ") ?? ""}</div>
        ),
      },
      {
        id: 9,
        label: "Insights",
        uid: "tags",
        width: "200px",
        filter: createAPIDropdownFilter({
          filterType: FilterType.API_DROPDOWN,
          searchKey: "tags",
          apiFunction: getTagsFilter,
          pageSize: 50,
          seeMore: true,
          extractorFunction: data => {
            return (
              data?.tags?.map((t: QueryTags) => ({
                label: <InsightLabel tag={t} />,
                value: t.name,
                showTooltip: false,
              })) || []
            );
          },
        }),
        render: item => <ColorInsightTags tags={item.tags} showMore />,
      },
      {
        id: 10,
        label: "Instance",
        uid: "instance_id",
        width: "150px",
        filter: {
          filterType: FilterType.DROPDOWN,
          searchKey: "instance",
          options: datastores.map(datastore => ({
            label: datastore.name,
            value: datastore.instance_id || "",
          })),
        },
        render: item => {
          return (
            <div className={cn("al-break-words", "lines-2")}>
              {getInstanceName(item.instance_id || "", datastores)}
            </div>
          );
        },
      },
    ],
    [currency, datastores]
  );

  return (
    <Stack direction="column" className="al-bg-white al-rounded-md">
      <GroupsHeader />
      <Stack direction="column" className="al-p-3" gap="md">
        <Stack direction="row" justify="end" align="center" gap="md">
          <GroupsDownload />
          <div id="columnpicker-container" />
        </Stack>
        <DataTable<QueryGroup>
          header={headers}
          data={groups}
          resetPage={resetPage}
          getRowUrl={getRowUrl}
          getRowState={getRowState}
          applicableFor={FilterApplicableFor.QUERY_GROUP}
          columnPickerPortal="columnpicker-container"
          columnPickerProps={{ buttonProps: { variant: "outline" } }}
          isLoading={isLoading}
          portals={{
            filter: "filter-container",
            savedFilters: "saved-filters-container",
          }}
        />
        <Paginate
          itemCount={totalItems}
          page={page}
          pageSize={pageSize}
          numPages={totalPages}
          onPageClick={setPage}
          onPageSizeChange={updatePageSize}
          showPageSize
          isLoading={isLoading}
        />
      </Stack>
    </Stack>
  );
};

export default GroupsListWithFilter;
